import { Box, Button, Group, LoadingOverlay, Stack, Table, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { first, isEmpty, partition } from 'remeda';
import { useAcceptGcollectRates } from 'shared/hooks/use-accept-gcollect-rates';
import { useLoadGcollectCaseFee } from 'shared/hooks/use-load-gcollect-case-fee';
import { useSendToThirdParty } from 'shared/hooks/use-send-to-third-party';
import { useLocalizedCurrencyFormatter } from 'shared/utils/currency';
import { invoiceActions } from 'store/invoice/invoice.actions';
import type { Id } from 'types';

type GcollectCaseError = {
  id: Id;
  error_message: string;
  key: string;
};

type GcollectResult = {
  caseIds: Array<number>;
  errors?: Array<GcollectCaseError>;
};

interface GcollectModalProps {
  invoiceIds: Array<Id>;
  gcollectId: Id;
  additionalInvoiceIds?: Array<Id>;
}

export function GcollectModal({
  invoiceIds,
  gcollectId,
  additionalInvoiceIds = [],
}: GcollectModalProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const [result, setResult] = useState<GcollectResult | null>(null);
  const resultRef = useRef<GcollectResult | null>(null);
  const hasActedRef = useRef(false);
  const { sendToThirdParty } = useSendToThirdParty();
  const { gcollectCaseFee, isGcollectCaseFeeLoading } = useLoadGcollectCaseFee(
    result?.caseIds ?? [],
  );
  const { acceptGcollectRates, isAcceptGcollectRatesLoading } = useAcceptGcollectRates();
  const formatter = useLocalizedCurrencyFormatter();

  const isBatch = invoiceIds.length > 1;

  const openSingleCase = async () => {
    sendToThirdParty(
      {
        id: String(first(invoiceIds)!),
        data: {
          third_party_company_id: String(gcollectId),
          additional_invoices: additionalInvoiceIds,
        },
      },
      {
        onSuccess: data => {
          setResult({ caseIds: data.third_party_case_ids });
        },
      },
    );
  };

  const openBatchCase = async () => {
    invoiceActions.actionsBatchAction({
      data: {
        third_party_company_id: gcollectId,
        action_type: 'third_party',
        invoice_ids: invoiceIds,
      },
      callback: (res: any) => {
        const [cases, errors] = partition(res, (x: any) => x.third_party_case_id != null);
        setResult({
          caseIds: cases.map(({ third_party_case_id }) => third_party_case_id),
          errors: errors as Array<GcollectCaseError>,
        });
      },
    });
  };

  useEffect(() => {
    if (isBatch) openBatchCase();
    else openSingleCase();
  }, []);

  useEffect(() => {
    resultRef.current = result;
  }, [result]);

  useEffect(() => {
    return () => {
      if (resultRef.current?.caseIds?.length && !hasActedRef.current) {
        acceptGcollectRates({ thirdPartyCaseIds: resultRef.current.caseIds, acceptRates: false });
      }
    };
  }, []);

  const isLoading = result == null || isGcollectCaseFeeLoading || isAcceptGcollectRatesLoading;

  const caseRows = (gcollectCaseFee?.invoices ?? []).map(
    ({ reference, collectionFee, gcollectCollectionRate, remainingAmount }) => [
      reference,
      formatter.format(remainingAmount),
      `${Number((gcollectCollectionRate * 100).toFixed(2))} %`,
      formatter.format(collectionFee),
    ],
  );

  const errors = result?.errors ?? [];
  const errorRows = errors.map(({ key, error_message }) => [key, error_message]);

  return (
    <Box pos="relative">
      <LoadingOverlay visible={isLoading} />
      <Stack gap="md">
        {!isEmpty(caseRows) && (
          <>
            <Text>{t(i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.GCOLLECT.VALIDATE_FEES_TEXT)}</Text>
            <Table
              w="100%"
              striped
              style={{ border: '1px solid lightgrey' }}
              mt="lg"
              data={{
                head: [
                  t(i18nKeys.COMMON.INVOICE),
                  t(i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.GCOLLECT.INVOICE_AMOUNT),
                  t(i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.GCOLLECT.COLLECTION_PERCENTAGE),
                  t(i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.GCOLLECT.COLLECTION_AMOUNT),
                ],
                body: caseRows,
              }}
            />
          </>
        )}
        {!isEmpty(errors) && (
          <>
            <Text mt="lg">
              {t(i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.GCOLLECT.VALIDATE_FEES_ERRORS)}
            </Text>
            <Table
              w="100%"
              striped
              style={{ border: '1px solid lightgrey' }}
              data={{
                head: [t(i18nKeys.COMMON.INVOICE), t(i18nKeys.REASON)],
                body: errorRows,
              }}
            />
          </>
        )}
        <Group gap="md" justify="end">
          {isEmpty(caseRows) ? (
            <Button color="red" onClick={modals.closeAll}>
              {t(i18nKeys.CLOSE)}
            </Button>
          ) : (
            <>
              <Button
                variant="subtle"
                color="red"
                disabled={isAcceptGcollectRatesLoading}
                onClick={() => {
                  hasActedRef.current = true;
                  acceptGcollectRates(
                    { thirdPartyCaseIds: result!.caseIds, acceptRates: false },
                    { onSuccess: () => modals.closeAll() },
                  );
                }}
              >
                {t(i18nKeys.CANCEL)}
              </Button>
              <Button
                loading={isAcceptGcollectRatesLoading}
                onClick={() => {
                  hasActedRef.current = true;
                  acceptGcollectRates(
                    { thirdPartyCaseIds: result!.caseIds, acceptRates: true },
                    {
                      onSuccess: () => {
                        history.push('/thirdparty-cases');
                        modals.closeAll();
                      },
                    },
                  );
                }}
              >
                {t(i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.GCOLLECT.ACCEPT)}
              </Button>
            </>
          )}
        </Group>
      </Stack>
    </Box>
  );
}
